import {
  Box,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Grid,
  Typography,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { ContainedButton } from 'components/ContainedButton';
import SelectField from 'components/select-field';
import TextField from 'components/text-field';
import React, { FC, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { template_document } from '../types';
import { useTranslation } from 'react-i18next';

export type FileDataType = {
	kind: string;
	description: string;
	template: string;
	per: string[];
};

const useStyles = makeStyles((theme: any) => ({
  title: {
    fontWeight: 500,
    padding: theme.spacing(2),
    color: theme.palette.primary.main,
    borderBottomStyle: 'solid',
    borderBottomWidth: 1,
    borderBottomColor: theme.accent.main,
    paddingBottom: theme.spacing(2)
  },
  actionButtons: { padding: theme.spacing(2) }
}));

type CreateFileModalType = {
	open: boolean;
	handleClose: (id: string) => void;
	handleCreateFile: (payload: any) => void;
	getTemplates: template_document[] | undefined;
	selectedKindTab: string
};

const getDocumentKindList = (t: any) => [
  {
    label: t('documents:template'),
    value: 'template'
  },
  {
    label: t('documents:upload'),
    value: 'upload'
  },
  {
    label: t('documents:pdf'),
    value: 'pdf'
  },
  {
    label: t('documents:zip'),
    value: 'zip'
  }
];

const getDocumentList = (t: any) => {
  return getDocumentKindList(t).sort((kindA, kindB) => kindA.value.localeCompare(kindB.value));
};

const CreateFileModal: FC<CreateFileModalType> = ({
  open,
  handleClose,
  handleCreateFile,
  getTemplates,
  selectedKindTab
}) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const handleCheckboxChange = (value: string[], per: string, onChange) => {
    if (value.includes(per)) {
      onChange(value.filter((val) => val !== per));
    } else {
      onChange([...value, per]);
    }
  };

  const {
    handleSubmit,
    control,
    watch,
    formState: { isValid },
    reset: resetForm,
    setValue
  } = useForm<FileDataType>({
    mode: 'onChange'
  });

  const kindValue = watch('kind');

  useEffect(() => {
    const nextKind = getDocumentList(t).some(({ value }) => value === selectedKindTab) ? selectedKindTab : '';
    if (kindValue !== nextKind) {
      setValue('kind', nextKind, { shouldDirty: true });
    }
  }, [selectedKindTab, kindValue, t, setValue]);

  const translatedTemplates = getTemplates?.map(template => ({ ...template, code: template._id })) || [];

  return (
    <Dialog open={open} onClose={() => handleClose('')} maxWidth="sm" fullWidth>
      <DialogTitle style={{ padding: "0px" }}>
        <Typography variant="h5" className={classes.title}>
          {t('documents:createNewDocument')}
        </Typography>
      </DialogTitle>
      <DialogContent style={{ padding: "1rem", maxWidth: "100%" }}>
        <Grid container direction="row" spacing={2}>
          <Grid item xs={12}>
            <Controller
              defaultValue=""
              control={control}
              name="description"
              rules={{ required: true }}
              render={({ field: { ref, ...field } }) => (
                <TextField
                  {...field}
                  inputRef={ref}
                  label={t('orders:descriptionLabel')}
                  fullWidth
                />
              )}
            />
          </Grid>
          <Grid item xs={12}>
            <Controller
              control={control}
              name="kind"
              rules={{ required: true }}
              render={({ field: { ref, value, ...field } }) => (
                <SelectField
                  label={t("documents:kind")}
                  inputRef={ref}
                  {...field}
                  value={value}
                  InputProps={{
									  ...field
                  }}
                  data={getDocumentList(t)}
                  dataKey="value"
                  dataValue="value"
                  dataText="label"
                />
              )}
            />
          </Grid>

          {getTemplates && watch('kind') === 'template' && (
          <>
            <Grid item xs={12}>
              <Controller
                control={control}
                name="template"
                rules={{ required: watch('kind') }}
                render={({ field: { ref, ...field } }) => (
                  <SelectField
                    label={t("documents:template")}
                    inputRef={ref}
                    {...field}
                    InputProps={{ ...field }}
                    data={translatedTemplates}
                    dataKey="_id"
                    dataValue="_id"
                    dataText="description"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl component="fieldset">
                <FormLabel component="legend">{t('documents:createFilePer')}</FormLabel>
                <Controller
                  control={control}
                  name="per"
                  defaultValue={[]}
                  render={({ field: { ref, ...field } }) => (
                    <FormGroup>
                      <FormControlLabel
                        control={(
                          <Checkbox
                            inputRef={ref}
                            value="property"
                            onChange={(e) => handleCheckboxChange(
															  field.value,
															  e.target.value,
															  field.onChange
                            )}
                          />
													)}
                        label={t('orders:propertyLabel')}
                      />
                      <FormControlLabel
                        disabled={field.value.includes('signer')}
                        control={(
                          <Checkbox
                            inputRef={ref}
                            value="party"
                            onChange={(e) => handleCheckboxChange(
															  field.value,
															  e.target.value,
															  field.onChange
                            )}
                          />
													)}
                        label={t('orders:partyLabel')}
                      />
                      <FormControlLabel
                        disabled={field.value.includes('party')}
                        control={(
                          <Checkbox
                            inputRef={ref}
                            value="signer"
                            onChange={(e) => handleCheckboxChange(
															  field.value,
															  e.target.value,
															  field.onChange
                            )}
                          />
													)}
                        label={t('orders:signerLabel')}
                      />
                    </FormGroup>
                  )}
                />
              </FormControl>
            </Grid>
          </>
          )}

          <Grid item xs={12} />
        </Grid>
      </DialogContent>
      <DialogActions className={classes.actionButtons}>
        <Box style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem" }}>
          <ContainedButton
            onClick={() => { handleClose(''); }}
            text={t('common:close')}
          />
          <ContainedButton
            onClick={handleSubmit((data) => {
						  handleCreateFile(data);
						  resetForm();
						  handleClose('');
            })}
            disabled={!isValid}
            text={t('documents:createDocument')}
          />
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default CreateFileModal;
